import React, { useState } from "react";
import styled from "@emotion/styled";
import TitledCard from "~/components/titled-card";
import Button from "~/components/button";
import { InlineProgress } from "~/components/inline-progress";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import { Table, TableCell, TableRow } from "~/table";
import { currencyFormatter } from "~/currencyFormatter";
import { useReportFetcher } from "~/report-fetcher/useReportFetcher";
import {
  CurrentYearOfficeBillingQuery,
  useCurrentYearOfficeBillingQuery,
} from "./officeBilling.hotchoc.generated";

type Summary = NonNullable<
  CurrentYearOfficeBillingQuery["officeBilling"]["currentYearSummary"]
>;
type Amount = Summary["yearToDate"]["amounts"][number];

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ScrollContainer = styled.div`
  overflow-x: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

// The doubled selector outranks the MDC typography class that sets the cell's font weight.
const TotalCell = styled(TableCell)`
  && {
    font-weight: bold;
  }
`;

const DetailsDialog = styled(Dialog)`
  .mdc-dialog__surface {
    max-width: min(90vw, 1200px);
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px 16px;
`;

const Title = "Per Office Billing";

const amountFor = (amounts: Amount[], officeCode: string) =>
  amounts.find((x) => x.officeCode === officeCode)?.amount ?? 0;

const AmountRow: React.FC<{
  label: string;
  amounts: number[];
  isTotalRow?: boolean;
  lastIsTotal?: boolean;
}> = ({ label, amounts, isTotalRow, lastIsTotal }) => {
  const LabelCell = isTotalRow ? TotalCell : TableCell;

  return (
    <TableRow>
      <LabelCell text={label} />
      {amounts.map((amount, idx) => {
        const isTotal =
          isTotalRow || (lastIsTotal && idx === amounts.length - 1);
        const Cell = isTotal ? TotalCell : TableCell;

        return (
          <Cell
            key={idx}
            text={currencyFormatter.format(amount)}
            align="right"
          />
        );
      })}
    </TableRow>
  );
};

type AmountColumn = {
  header: string;
  amounts: Amount[];
  total: number;
};

/**
 * The previous month, the current month and year to date. In January the previous month is in
 * the prior year, which the current-year summary does not cover, so that column is left out.
 */
const amountColumns = (summary: Summary): AmountColumn[] => {
  const months = summary.months.map((row) => ({
    header: monthNames[row.month - 1],
    amounts: row.amounts,
    total: row.total,
  }));

  return [
    ...months.slice(-2),
    { header: "Year to Date", ...summary.yearToDate },
  ];
};

const SummaryTable: React.FC<{ summary: Summary }> = ({ summary }) => {
  const columns = amountColumns(summary);

  return (
    <ScrollContainer>
      <Table
        columnWidths={`auto repeat(${columns.length}, minmax(6em, 1fr))`}
        columnCount={columns.length + 1}
      >
        <TableRow header>
          <TableCell text="Office" />
          {columns.map((column, idx) => {
            // The last column is Year to Date, which is bold like the totals.
            const Cell = idx === columns.length - 1 ? TotalCell : TableCell;

            return (
              <Cell key={column.header} text={column.header} align="right" />
            );
          })}
        </TableRow>
        {summary.officeCodes.map((officeCode) => (
          <AmountRow
            key={officeCode}
            label={officeCode}
            amounts={columns.map((column) =>
              amountFor(column.amounts, officeCode)
            )}
            lastIsTotal
          />
        ))}
        <AmountRow
          label="Total"
          amounts={columns.map((column) => column.total)}
          isTotalRow
        />
      </Table>
    </ScrollContainer>
  );
};

/**
 * Every month so far for every office. Offices are rows because there can be any number of them,
 * while months cap the columns at 14: Office, up to 12 months, and the year-to-date Total.
 */
const DetailsTable: React.FC<{ summary: Summary }> = ({ summary }) => {
  const monthCount = summary.months.length;

  return (
    <ScrollContainer>
      <Table
        columnWidths={`auto repeat(${monthCount + 1}, minmax(6em, 1fr))`}
        columnCount={monthCount + 2}
      >
        <TableRow header>
          <TableCell text="Office" />
          {summary.months.map((row) => (
            <TableCell
              key={row.month}
              text={monthNames[row.month - 1]}
              align="right"
            />
          ))}
          <TotalCell text="Total" align="right" />
        </TableRow>
        {summary.officeCodes.map((officeCode) => (
          <AmountRow
            key={officeCode}
            label={officeCode}
            amounts={[
              ...summary.months.map((row) =>
                amountFor(row.amounts, officeCode)
              ),
              amountFor(summary.yearToDate.amounts, officeCode),
            ]}
            lastIsTotal
          />
        ))}
        <AmountRow
          label="Total"
          amounts={[
            ...summary.months.map((row) => row.total),
            summary.yearToDate.total,
          ]}
          isTotalRow
        />
      </Table>
    </ScrollContainer>
  );
};

/**
 * This year's billing for the offices that currently allow projects. Whether the card is shown is
 * up to the caller; the summary is null when the user can view no invoices.
 */
export const OfficeBillingCard: React.FC = () => {
  const { data, loading } = useCurrentYearOfficeBillingQuery();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const download = useReportFetcher();

  if (loading) {
    return (
      <TitledCard title={Title}>
        <LoadingContainer data-testid="office-billing-loading">
          <InlineProgress />
        </LoadingContainer>
      </TitledCard>
    );
  }

  const summary = data?.officeBilling.currentYearSummary;

  if (!summary) {
    return null;
  }

  const title = `${Title} ${summary.year}`;
  const hasOffices = summary.officeCodes.length > 0;

  return (
    <TitledCard
      title={title}
      isEmpty={!hasOffices}
      emptyMessage="No offices currently allow projects"
      actions={
        hasOffices ? (
          <Button icon="table_view" onClick={() => setDetailsOpen(true)}>
            Details
          </Button>
        ) : undefined
      }
    >
      <SummaryTable summary={summary} />
      <DetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        portalize
      >
        <DialogTitle>{`${title} by Month`}</DialogTitle>
        <DialogContent>
          {detailsOpen && <DetailsTable summary={summary} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button
            primary
            disabled={downloading}
            onClick={() =>
              download({
                setLoading: setDownloading,
                path: "office-billing-csv",
                body: { year: summary.year, offices: summary.officeCodes },
                fileName: `office-billing-${summary.year}`,
                type: "text/csv;charset=utf-8;",
              })
            }
          >
            Download Report
          </Button>
        </DialogActions>
      </DetailsDialog>
    </TitledCard>
  );
};
